import { AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';

declare const google: any;

@Component({
  selector: 'app-google-place-autocomplete',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './google-place-autocomplete.component.html',
  styleUrls: ['./google-place-autocomplete.component.css']
})

export class GooglePlaceAutocompleteComponent
  implements AfterViewInit, OnDestroy {

  // Enable/disable Google Places API call logging.
  private readonly enableGooglePlacesLogging = true;
  private debounceTimer?: ReturnType<typeof setTimeout>;
  private autocompleteRequestCount = 0;

  // Runtime counters used to demonstrate the number of Google Places
  // requests made by this component. These are intentionally limited to
  // Places API calls made by this component; importLibrary is not counted
  // as a Places data request.
  /*
   * ZIP is read only from the data already returned by
   * AutocompleteSuggestion. No additional request is made
   * when a dropdown prediction does not contain ZIP.
   */
  private googlePlacesCallStats = {
    autocompleteCalls: 0,
    placeDetailsCalls: 0,
    placeDetailsSelectionFallbackCalls: 0
  };

  private googlePlacesLog(message: string, data?: any): void {

    if (!this.enableGooglePlacesLogging) {
      return;
    }
  }


  private logGooglePlacesSummary(reason: string): void {

    if (!this.enableGooglePlacesLogging) {
      return;
    }

    const autocompleteCalls =
      this.googlePlacesCallStats.autocompleteCalls;

    const placeDetailsCalls =
      this.googlePlacesCallStats.placeDetailsCalls;

    const totalCalls =
      autocompleteCalls + placeDetailsCalls;
  }


  // Call this from the browser console if a manual snapshot is required.
  // Example: component.googlePlacesCallStats is private, so the automatic
  // summary is the preferred method.



  // =========================================================

  // FORM

  // =========================================================



  @Input() formGroup!: FormGroup;
  @Input() address1ControlName = 'address1';
  @Input() cityControlName = 'city';
  @Input() stateControlName = 'state';
  @Input() zipCodeControlName = 'zip';

  // =========================================================

  // INPUT OPTIONS

  // =========================================================



  @Input() readonly = false;
  @Input() maxlength = 60;
  @Input() placeholder = '';
  @Input() placeSelectedCallback?: (place: any) => void;

  // =========================================================

  // OUTPUT

  // =========================================================

  @Output() addressBlur = new EventEmitter<void>();
  // =========================================================

  // VIEW

  // =========================================================



  @ViewChild('addressInput', { static: true })
  addressInput!: ElementRef<HTMLInputElement>;
  // =========================================================

  // PREDICTIONS

  // =========================================================

  predictions: any[] = [];
  showPredictions = false;
  activePredictionIndex = -1;

  // =========================================================

  // GOOGLE PLACES API

  // =========================================================



  private AutocompleteSuggestion: any;
  private AutocompleteSessionToken: any;
  private sessionToken: any;
  // =========================================================

  // SUBSCRIPTION

  // =========================================================
  private addressValueSubscription?: Subscription;
  // =========================================================

  // REQUEST CONTROL

  // =========================================================
  private requestSequence = 0;
  // =========================================================

  // CONSTRUCTOR

  // =========================================================
  constructor(

    private ngZone: NgZone

  ) { }
  // =========================================================

  // INITIALIZE

  // =========================================================



  async ngAfterViewInit(): Promise<void> {
    setTimeout(async () => {
      try {
        this.googlePlacesLog(
          'CALL -> google.maps.importLibrary(\'places\')'
        );

        const {

          AutocompleteSuggestion,

          AutocompleteSessionToken

        } = await google.maps.importLibrary('places');

        this.googlePlacesLog(
          'RETURN <- google.maps.importLibrary(\'places\')',
          {
            AutocompleteSuggestionLoaded: !!AutocompleteSuggestion,
            AutocompleteSessionTokenLoaded: !!AutocompleteSessionToken
          }
        );
        this.AutocompleteSuggestion = AutocompleteSuggestion;
        this.AutocompleteSessionToken = AutocompleteSessionToken;
        this.createSession();
        this.syncAddressValue();
        this.subscribeToAddressChanges();
        setTimeout(() => {
          this.syncAddressValue();
        }, 500);
      } catch (error) {
        console.error(
          'Unable to initialize Google Places Autocomplete:',
          error
        );
      }
    }, 100);
  }


  // =========================================================

  // CREATE SESSION

  // =========================================================



  private createSession(): void {
    if (!this.AutocompleteSessionToken) {
      return;
    }
    this.sessionToken = new this.AutocompleteSessionToken();
    this.googlePlacesLog('NEW Autocomplete session token created');
  }

  // =========================================================

  // SUBSCRIBE TO FORM ADDRESS

  // =========================================================

  private subscribeToAddressChanges(): void {
    if (!this.formGroup) {
      return;
    }
    const control =
      this.formGroup.get(
        this.address1ControlName
      );
    if (!control) {
      return;
    }
    this.addressValueSubscription =
      control.valueChanges.subscribe(
        (value: any) => {
          /*

           * Do not overwrite what the user is

           * currently typing.

           */

          if (document.activeElement === this.addressInput?.nativeElement) {
            return;
          }
          this.ngZone.run(() => {
            if (this.addressInput?.nativeElement) {
              this.addressInput.nativeElement.value =
                value || '';
            }
          });
        }
      );
  }

  // =========================================================

  // SYNC FORM VALUE TO INPUT

  // =========================================================

  private syncAddressValue(): void {
    if (!this.addressInput?.nativeElement) {
      return;
    }

    const control =
      this.formGroup?.get(
        this.address1ControlName
      );

    if (!control) {
      return;
    }

    this.addressInput.nativeElement.value = control.value || '';
  }

  // =========================================================

  // INPUT

  // =========================================================

  async onInput(event: Event): Promise<void> {
    if (this.readonly) {
      return;
    }

    const input = event.target as HTMLInputElement;
    const value = input.value.trim();

    // Keep address1 synchronized with the form.
    const addressControl = this.formGroup?.get(
      this.address1ControlName
    );

    if (addressControl) {
      addressControl.setValue(input.value, {
        emitEvent: false
      });
    }

    this.activePredictionIndex = -1;

    // Cancel any pending request.
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Invalidate responses for previous input.
    this.requestSequence++;

    // Require at least 2 characters.
    if (value.length < 2) {
      this.predictions = [];
      this.showPredictions = false;
      return;
    }

    // Wait 400 ms after the last keystroke.
    this.debounceTimer = setTimeout(() => {
      void this.loadPredictions(value);
    }, 400);
  }


  // =========================================================
  // FOCUS
  // =========================================================

  async onFocus(): Promise<void> {
    if (this.readonly) {
      return;
    }

    const value =
      this.addressInput?.nativeElement?.value?.trim() || '';

    // If predictions are already available, simply show them.
    if (this.predictions.length > 0) {
      this.showPredictions = true;
      return;
    }

    // Match the current onInput minimum of 2 characters.
    if (value.length >= 2) {
      await this.loadPredictions(value);
    }
  }


  // =========================================================
  // BLUR
  // =========================================================

  onBlur(): void {
    // Delay hiding the dropdown so that clicking a prediction
    // is processed before the dropdown disappears.
    setTimeout(() => {
      this.showPredictions = false;

      this.ngZone.run(() => {
        this.addressBlur.emit();
      });
    }, 300);
  }


  // =========================================================

  // LOAD PREDICTIONS

  // =========================================================



  private async loadPredictions(
    input: string
  ): Promise<void> {

    const currentRequest =
      ++this.requestSequence;

    try {

      const request: any = {
        input,
        includedRegionCodes: ['us'],
        sessionToken: this.sessionToken
      };

      this.googlePlacesCallStats.autocompleteCalls++;

      this.googlePlacesLog(
        'CALL -> AutocompleteSuggestion.fetchAutocompleteSuggestions',
        {
          totalAutocompleteCalls:
            this.googlePlacesCallStats.autocompleteCalls,
          requestSequence: currentRequest,
          input,
          includedRegionCodes:
            request.includedRegionCodes,
          hasSessionToken:
            !!request.sessionToken
        }
      );

      this.autocompleteRequestCount++;

      console.count('[Places Test] Autocomplete API request');

      console.log('[Places Test] Request details:', {
        requestNumber: this.autocompleteRequestCount,
        input,
        timestamp: new Date().toISOString(),
        hasSessionToken: !!this.sessionToken
      });


      const result =
        await this.AutocompleteSuggestion
          .fetchAutocompleteSuggestions(request);

      this.googlePlacesLog(
        'RETURN <- AutocompleteSuggestion.fetchAutocompleteSuggestions',
        {
          requestSequence: currentRequest,
          suggestionCount:
            result?.suggestions?.length || 0
        }
      );

      if (
        currentRequest !==
        this.requestSequence
      ) {
        this.googlePlacesLog(
          'IGNORED stale autocomplete response',
          {
            requestSequence: currentRequest,
            latestRequestSequence:
              this.requestSequence
          }
        );

        return;
      }

      /*
       * IMPORTANT:
       *
       * We do NOT call toPlace() or fetchFields()
       * for dropdown predictions.
       *
       * We use only the information already returned
       * by AutocompleteSuggestion.
       *
       * If Google's prediction text already contains
       * a ZIP/postal code, it will be displayed.
       * If it does not, we do NOT make another API
       * call just to obtain the ZIP.
       */
      const suggestions =
        (result?.suggestions || [])
          .filter(
            (suggestion: any) =>
              !!suggestion?.placePrediction
          )
          .slice(0, 5);

      const displayDetails =
        suggestions.map(
          (suggestion: any, index: number) => {

            const prediction =
              suggestion.placePrediction;

            const mainText =
              prediction?.mainText?.text || '';

            const secondaryText =
              prediction?.secondaryText?.text || '';

            const fullText =
              prediction?.text?.text || '';

            // const zip =
            //   this.extractZipFromText(
            //     `${mainText}, ${secondaryText}, ${fullText}`
            //   );

            return {
              index,
              placeId:
                prediction?.placeId || null,
              mainText,
              secondaryText,
              fullText,
              zipFoundInAutocomplete: '',
              zip: null
            };

          }
        );

      this.googlePlacesLog(
        'Autocomplete prediction display data',
        {
          displayedPredictionCount:
            suggestions.length,
          predictionsWithZip:
            displayDetails.filter(
              (item: any) => item.zipFoundInAutocomplete
            ).length,

          predictionsWithoutZip:
            displayDetails.filter(
              (item: any) => !item.zipFoundInAutocomplete
            ).length,
          dropdownPlaceDetailsCalls: 0,
          predictions: displayDetails
        }
      );

      this.ngZone.run(() => {

        this.predictions =
          suggestions;

        this.showPredictions =
          this.predictions.length > 0;

        this.activePredictionIndex = -1;

      });

      this.googlePlacesLog(
        'Dropdown predictions ready',
        {
          displayedPredictionCount:
            suggestions.length,
          dropdownPlaceDetailsCalls: 0
        }
      );

    } catch (error) {

      console.error(
        'Error loading Google address predictions:',
        error
      );

      this.ngZone.run(() => {

        this.predictions = [];
        this.showPredictions = false;

      });

    }

  }

  /**
   * Attempts to find a US ZIP code in text that Google
   * already returned as part of the autocomplete prediction.
   *
   * This method does NOT make any Google API call.
   */
  private extractZipFromText(
    value: string
  ): string {

    if (!value) {
      return '';
    }

    const match =
      value.match(
        /\b\d{5}(?:-\d{4})?\b/
      );

    return match?.[0] || '';

  }

  // =========================================================
  // KEYBOARD

  // =========================================================



  onKeyDown(

    event: KeyboardEvent

  ): void {
    if (!this.showPredictions || this.predictions.length === 0) {
      return;
    }

    // Arrow Down

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.activePredictionIndex++;
      if (this.activePredictionIndex >= this.predictions.length) {

        this.activePredictionIndex = 0;
      }
      return;
    }

    // Arrow Up

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activePredictionIndex--;
      if (this.activePredictionIndex < 0) {
        this.activePredictionIndex = this.predictions.length - 1;
      }
      return;
    }

    // Enter

    if (event.key === 'Enter') {
      event.preventDefault();
      if (this.activePredictionIndex >= 0 && this.activePredictionIndex < this.predictions.length) {
        this.selectPrediction(
          this.predictions[this.activePredictionIndex], event);
      }
      return;
    }

    // Escape

    if (event.key === 'Escape') {
      event.preventDefault();
      this.showPredictions = false;
      this.activePredictionIndex = -1;
    }
  }

  // =========================================================

  // SELECT PREDICTION

  // =========================================================

  async selectPrediction(suggestion: any, event?: Event): Promise<void> {
    if (event) {
      event.preventDefault();
    }
    try {
      this.googlePlacesLog(
        'SELECT prediction',
        {
          predictionText: suggestion?.placePrediction?.text?.text ||
            suggestion?.streetAddress || '',
          placeId: suggestion?.placePrediction?.placeId || null,
          postalCode: suggestion?.postalCode || null
        }
      );

      /*
       * Fetch address components only after the user selects
       * a prediction. This is the only Place Details call
       * made by this component for the selected address.
       */
      const placePrediction =
        suggestion?.placePrediction;

      if (!placePrediction) {
        return;
      }

      const place =
        placePrediction.toPlace();

      this.googlePlacesCallStats.placeDetailsCalls++;

      this.googlePlacesLog(
        'CALL -> Place.fetchFields after prediction selection',
        {
          totalPlaceDetailsCalls: this.googlePlacesCallStats.placeDetailsCalls,
          placeId: place?.id || null,
          predictionText:
            placePrediction?.text?.text ||
            placePrediction?.mainText?.text ||
            '',
          fields: ['addressComponents']
        }
      );

      await place.fetchFields({
        fields: ['addressComponents']
      });

      this.googlePlacesLog(
        'RETURN <- Place.fetchFields after prediction selection',
        {
          placeId: place?.id || null,
          addressComponentCount: place?.addressComponents?.length || 0
        }
      );

      const components =
        place.addressComponents || [];

      // =======================================================

      // FIND ADDRESS COMPONENT

      // =======================================================



      const getComponent = (type: string): any => {
        return components.find((component: any) =>
          component.types?.includes(type)
        );
      };


      // =======================================================

      // ADDRESS 1

      // =======================================================



      const streetNumber = getComponent('street_number')?.longText || '';

      const route = getComponent('route')?.longText || '';

      const address1 = `${streetNumber} ${route}`.trim();


      // =======================================================

      // CITY

      // =======================================================



      const city = getComponent('locality')?.longText ||
        getComponent('sublocality')?.longText ||
        getComponent('postal_town')?.longText || '';

      // =======================================================

      // STATE

      // =======================================================



      const stateCode = getComponent('administrative_area_level_1')?.shortText || '';

      // =======================================================

      // ZIP CODE

      // =======================================================



      const zip = getComponent('postal_code')?.longText || '';
      this.googlePlacesLog('Selected address resolved', { address1, city, stateCode, zip });


      // =======================================================

      // PATCH FORM

      // =======================================================

      const patchValue: any = {};
      const addressControl = this.formGroup?.get(this.address1ControlName);

      if (addressControl) { patchValue[this.address1ControlName] = address1; }

      const cityControl = this.formGroup?.get(this.cityControlName);

      if (cityControl) { patchValue[this.cityControlName] = city; }

      const stateControl = this.formGroup?.get(this.stateControlName);
      if (stateControl) { patchValue[this.stateControlName] = stateCode; }
      const zipControl = this.formGroup?.get(this.zipCodeControlName);
      if (zipControl) { patchValue[this.zipCodeControlName] = zip; }

      // =======================================================

      // PATCH VALUES

      // =======================================================

      this.formGroup.patchValue(patchValue);

      // =======================================================

      // UPDATE ADDRESS INPUT

      // =======================================================


      if (this.addressInput?.nativeElement) {
        this.addressInput.nativeElement.value = address1;
      }


      // =======================================================

      // CLOSE DROPDOWN

      // =======================================================



      this.predictions = [];
      this.showPredictions = false;
      this.activePredictionIndex = -1;

      // =======================================================

      // CALLBACK

      // =======================================================


      if (this.placeSelectedCallback) {
        /*
  
         * Create/fetch the Place only for the callback.
  
         */

        const placePrediction = suggestion?.placePrediction;

        if (placePrediction) {
          const place = placePrediction.toPlace();

          this.googlePlacesLog(
            'PLACE callback -> placePrediction.toPlace()',
            { placeId: place?.id || null }
          );

          this.placeSelectedCallback(place);
        }
      }





      // =======================================================

      // NEW SESSION

      // =======================================================



      this.createSession();
      // Print a client-friendly request summary after a completed selection.
      this.logGooglePlacesSummary('Address selected');


    } catch (error) {
      console.error('Error selecting Google address:', error);
    }
  }





  // =========================================================
  // MAIN DISPLAY TEXT
  // =========================================================

  getPredictionMainText(
    suggestion: any
  ): string {

    return (
      suggestion?.placePrediction?.mainText?.text ||
      ''
    );

  }


  // =========================================================
  // SECONDARY DISPLAY TEXT
  // =========================================================

  getPredictionSecondaryText(suggestion: any): string {
    const prediction = suggestion?.placePrediction;

    if (!prediction) {
      return '';
    }

    return prediction?.secondaryText?.text || '';
  }

  // =========================================================
  // DESTROY

  // =========================================================



  ngOnDestroy(): void {

    //     console.log(
    //   '[Places Test] Total autocomplete requests:',
    //   this.autocompleteRequestCount
    // );

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.addressValueSubscription?.unsubscribe();
  }



}